/**
 * ItemContextMenu: the one menu for an item, wherever it is (the side panel
 * tree's ⋮, and right-click on tiles, cards and rows). Presentational: the
 * caller hands it entries (what each action does, and whether the target set
 * allows it) and it owns the order, the words, the icons and the grouping.
 *
 * An entry that is absent is hidden (the surface can't offer it at all, e.g.
 * a read-only role); an entry with `enabled: false` stays visible with its
 * reason (the target set doesn't allow it right now, same rule as the action
 * bar). Three triggers: a point on screen for right-click and long-press (the
 * caller controls open and close), the top bar's ⋮ for the open collection
 * (or, at the workspace root, its one Create in item), or a detail page's
 * "More options" ⋮ (`DetailActionMenu`).
 */

import { Fragment, useRef, type ReactNode, type RefObject } from 'react';
import { Box, IconButton, Menu, MenuButton, MenuDivider, MenuList, Portal } from '@chakra-ui/react';

import { MaterialIcon } from '../../ui/MaterialIcon';
import { ItemMenuEntryItem } from './ItemMenuEntryItem';
import { PointMenu } from './PointMenu';
import { ITEM_MENU_GROUPS, ITEM_MENU_ICONS, ITEM_MENU_LABELS } from './itemMenuEntries';
import type { ItemMenuActionId, ItemMenuEntry, ItemMenuSlotId } from './itemMenuEntries';
import type { ItemMenuTrigger, ItemMenuUiSurface } from './itemMenuStore';

type Trigger =
  | {
      readonly kind: 'detail';
      /** The page returns focus here after a dialog the menu opened. */
      readonly menuButtonRef: RefObject<HTMLButtonElement | null>;
      /** Each open of the page's ⋮, with how it was raised (analytics). */
      readonly onOpen?: (trigger: ItemMenuTrigger) => void;
    }
  | {
      readonly kind: 'point';
      readonly x: number;
      readonly y: number;
      readonly onClose: () => void;
    }
  | {
      readonly kind: 'topbar';
      /** The button's accessible name. */
      readonly label: string;
      /** Each open, with how it was raised (analytics). */
      readonly onOpen?: (trigger: ItemMenuTrigger) => void;
    };

interface ItemContextMenuProps {
  readonly trigger: Trigger;
  readonly entries: ReadonlyArray<ItemMenuEntry>;
  /** Every target is already a favorite: the entry offers removal. */
  readonly isFavorite?: boolean;
  /** The "Create in…" item (folders, or the workspace root's). */
  readonly addSubmenu?: ReactNode;
  /** The Manage access item (one target only). */
  readonly accessItem?: ReactNode;
  /**
   * Show the keys that do Open and Delete from a focused item (board
   * `Context`). Only a desktop canvas view asks: a phone has no keyboard and
   * the tree's rows do not take those keys.
   */
  readonly shortcutHints?: boolean;
  /** Below the entries, after a divider: the top bar's Created by / dates. */
  readonly footer?: ReactNode;
  /** Sent with each chosen action (ITEM_CONTEXT_ACTION); the top bar's and detail pages' ⋮ only. */
  readonly surface?: ItemMenuUiSurface;
}

/** Return and Backspace (the Mac Delete key); the canvas takes Delete too. */
const SHORTCUTS: Partial<Record<ItemMenuActionId, string>> = {
  open: '\u21B5',
  delete: '\u232B',
};

export function ItemContextMenu({
  trigger,
  entries,
  isFavorite = false,
  shortcutHints = false,
  footer,
  surface,
  ...slots
}: ItemContextMenuProps) {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const render = (id: ItemMenuActionId | ItemMenuSlotId): ReactNode => {
    if (id === 'add') return slots.addSubmenu;
    if (id === 'access') return slots.accessItem;
    const entry = byId.get(id);
    if (!entry) return null;
    const favorite = id === 'favorite' && isFavorite;
    const label = favorite ? 'Remove from favorites' : (entry.label ?? ITEM_MENU_LABELS[id]);
    return (
      <ItemMenuEntryItem
        entry={entry}
        label={label}
        icon={ITEM_MENU_ICONS[id]}
        filled={favorite}
        shortcut={shortcutHints ? SHORTCUTS[id] : undefined}
        surface={surface}
      />
    );
  };
  const groups = ITEM_MENU_GROUPS.map((ids) =>
    ids.flatMap((id) => {
      const node = render(id);
      return node ? [<Fragment key={id}>{node}</Fragment>] : [];
    }),
  ).filter((group) => group.length > 0);

  const list = (
    <Portal>
      {/* Stop propagation so item clicks don't bubble through the React tree
          (React 19 root delegation) into the row's own click handler. */}
      <Box onClick={(e) => e.stopPropagation()}>
        <MenuList
          zIndex="modal"
          minW="188px"
          maxH="calc(100vh - 120px)"
          overflowY="auto"
          fontSize="sm"
        >
          {groups.map((group, index) => (
            <Fragment key={index}>
              {index > 0 && <MenuDivider />}
              {group}
            </Fragment>
          ))}
          {footer && (
            <>
              {groups.length > 0 && <MenuDivider />}
              {footer}
            </>
          )}
        </MenuList>
      </Box>
    </Portal>
  );

  if (trigger.kind === 'point') {
    return <PointMenu {...trigger}>{list}</PointMenu>;
  }
  if (trigger.kind === 'topbar') {
    return <TopBarMenu {...trigger}>{list}</TopBarMenu>;
  }

  return <DetailMenu {...trigger}>{list}</DetailMenu>;
}

/** Remembers how the ⋮ was pressed: a pointer press, else the keyboard. */
function useOpenedBy(onOpen?: (trigger: ItemMenuTrigger) => void) {
  const openedBy = useRef<ItemMenuTrigger>('keyboard');
  return {
    menuProps: {
      onOpen: () => {
        onOpen?.(openedBy.current);
        openedBy.current = 'keyboard';
      },
      // A pointer press that closes the menu opens nothing to reset it.
      onClose: () => {
        openedBy.current = 'keyboard';
      },
    },
    onPointerDown: () => {
      openedBy.current = 'pointer';
    },
  };
}

/** A detail page's "More options" ⋮ (`DetailActionMenu`). */
function DetailMenu({
  menuButtonRef,
  onOpen,
  children,
}: {
  menuButtonRef: RefObject<HTMLButtonElement | null>;
  onOpen?: (trigger: ItemMenuTrigger) => void;
  children: ReactNode;
}) {
  const { menuProps, onPointerDown } = useOpenedBy(onOpen);
  return (
    <Menu isLazy placement="bottom-end" {...menuProps}>
      <MenuButton
        ref={menuButtonRef}
        as={IconButton}
        aria-label="More options"
        icon={<MaterialIcon name="more_vert" />}
        size="sm"
        variant="topbar"
        flexShrink={0}
        onPointerDown={onPointerDown}
      />
      {children}
    </Menu>
  );
}

/**
 * The top bar's ⋮ (its `topbar` button style). An open raised by a pointer
 * press reports `pointer`; Enter, Space or an arrow key on the button,
 * `keyboard`.
 */
function TopBarMenu({
  label,
  onOpen,
  children,
}: {
  label: string;
  onOpen?: (trigger: ItemMenuTrigger) => void;
  children: ReactNode;
}) {
  const { menuProps, onPointerDown } = useOpenedBy(onOpen);
  return (
    <Menu isLazy placement="bottom-end" {...menuProps}>
      <MenuButton
        as={IconButton}
        aria-label={label}
        icon={<MaterialIcon name="more_vert" boxSize={5} />}
        variant="topbar"
        size="sm"
        px={0}
        w="32px"
        flexShrink={0}
        onPointerDown={onPointerDown}
      />
      {children}
    </Menu>
  );
}
